import React from "react";
import ReactDOM from "react-dom/client";
import axios from "axios";
import { BrowserRouter } from "react-router-dom";
import { Provider } from "react-redux";
import { PostHogProvider } from "@posthog/react";
import { NuqsAdapter } from "nuqs/adapters/react-router/v7";
import { registerSW } from "virtual:pwa-register";
import { toast } from "sonner";
import store from "./store";
import { QueryProvider } from "./providers/QueryProvider";
import { AuthProvider } from "./providers/AuthProvider";
import { Toaster } from "./components/ui/sonner";
import { UploadProgressProvider } from "./providers/UploadProgressProvider";
import posthog from "./lib/posthogClient";
import { ErrorBoundary } from "./components/ErrorBoundary";
import App from "./App";

import "./index.css";

// Global error handler - captures unhandled JS errors with full stack trace
// Also handles stale chunk references caused by SW serving old HTML after a deploy
window.addEventListener("error", (event) => {
  const isMissingChunk =
    event.message?.includes("dynamically imported module") ||
    event.message?.includes("Loading chunk") ||
    event.message?.includes("Failed to fetch");
  if (isMissingChunk) {
    console.warn("[ChunkError] Stale chunk reference — reloading");
    window.location.reload();
    return;
  }

  console.error("[GlobalError]", event.error?.message, event.error?.stack);
  posthog.capture("frontend_unhandled_error", {
    error_message: event.error?.message || event.message,
    error_stack: event.error?.stack,
    filename: event.filename,
    lineno: event.lineno,
    colno: event.colno,
    url: window.location.href,
  });
});

window.addEventListener("unhandledrejection", (event) => {
  const reason = event.reason;

  // Skip Axios errors that are expected/managed — these are handled by TanStack Query,
  // the auth refresh interceptor, or are transient user-side network conditions.
  if (axios.isCancel(reason)) return; // request cancelled (component unmounted, navigation)
  if (axios.isAxiosError(reason)) {
    const status = reason.response?.status;
    if (status === 401) return; // handled transparently by the refresh interceptor
    if (status === 404) return; // expected — stale links, missing resources
    // Network-level failures (timeout, offline) — transient, not actionable as an exception
    if (reason.code === "ECONNABORTED" || reason.code === "ERR_NETWORK") return;
  }

  console.error("[UnhandledRejection]", reason);
  posthog.capture("frontend_unhandled_rejection", {
    reason: String(reason),
    stack: reason?.stack,
    url: window.location.href,
  });
});

// Register service worker with auto-update
registerSW({
  onNeedRefresh() {
    toast("Update available", {
      description: "A new version is available. Reloading...",
      duration: 3000,
    });
    setTimeout(() => window.location.reload(), 2000);
  },
  onOfflineReady() {
    console.log("App ready to work offline");
  },
  onRegisteredSW(_swUrl, registration) {
    if (!registration) return;

    const attemptUpdate = () => {
      registration.update().catch(async (err: unknown) => {
        const msg = err instanceof Error ? err.message : String(err);
        if (msg.includes("redirect")) {
          console.warn("[SW] Script is behind a redirect — unregistering stale SW");
          await registration.unregister();
          const cacheKeys = await caches.keys();
          await Promise.all(cacheKeys.map((key) => caches.delete(key)));
          window.location.reload();
        }
      });
    };

    // Check for updates every 2 minutes and on tab focus
    setInterval(attemptUpdate, 2 * 60 * 1000);
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") attemptUpdate();
    });
  },
  onRegisterError(error) {
    console.warn("Service worker registration failed:", error);
  },
});

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <ErrorBoundary>
      <Provider store={store}>
        <QueryProvider>
          <AuthProvider>
            <BrowserRouter>
              <PostHogProvider client={posthog}>
                <NuqsAdapter>
                  <UploadProgressProvider>
                    <App />
                    <Toaster position="bottom-right" />
                  </UploadProgressProvider>
                </NuqsAdapter>
              </PostHogProvider>
            </BrowserRouter>
          </AuthProvider>
        </QueryProvider>
      </Provider>
    </ErrorBoundary>
  </React.StrictMode>
);
